/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useForm } from 'react-hook-form';

import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { actionHelpers } from 'app-components/flux/core';
import { sharesActions, sharesTypes } from 'app-components/flux/shares';
import { getLogger, tracker } from 'app-components/utils/core';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogForm from 'components/Dialogs/StdDialogForm';

import addShareImage from 'assets/illustrations/Sharing.png';

import TextField from 'components/ReactFormHook/TextField';

const log = getLogger('shares/AddShareDialog');

const AddUserDialog = ({ dataset, onClose, shareCounts }) => {
  const dispatch = useDispatch();

  const [share, setShare] = useState(null);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { first_name: '', last_name: '', email: '' } });

  const onSubmit = async (data) => {
    const createSharePromise = actionHelpers.bindPromiseAction(dispatch, sharesActions.createShare);
    const invite = sharesTypes.mkShare({
      clientId: uuid(),
      datasetId: dataset.id,
      granteeFirstName: data.first_name,
      granteeLastName: data.last_name,
      granteeEmail: data.email,
      status: 'INVITED',
    });
    await createSharePromise(invite)
      .then((response) => {
        tracker.track(tracker.events.inviteMember, { status: 'success' });
        setShare(response.resource);
      })
      .catch((error) => {
        log.error('Error creating share', error);
        tracker.track(tracker.events.viewSpaces, { status: 'failure' });
      });
  };

  const renderAddShareForm = () => (
    <StdDialogForm onSubmit={handleSubmit(onSubmit)}>
      <StdDialogContent
        id='add_share_dialog_content'
        sx={{ display: 'flex', alignItems: 'start', flexDirection: 'row' }}
      >
        <img alt='' src={addShareImage} width='168px' height='auto' />
        <Box display='flex' flex='1' flexDirection='column' ml={3} mr={13}>
          <Typography variant='displayRegular'>{dataset.name}</Typography>
          <Typography variant='captionRegular'>
            {shareCounts[1] - shareCounts[0]}/{shareCounts[1]} invitations left
          </Typography>
          <Box display='flex' mt={2}>
            <TextField
              name='first_name'
              control={control}
              label='First Name'
              rules={{ required: true }}
              variant='filled'
            />
            <TextField
              name='last_name'
              control={control}
              label='Last Name'
              rules={{ required: true }}
              sx={{ ml: 2 }}
              variant='filled'
            />
          </Box>
          <TextField
            name='email'
            control={control}
            helperText="This will be the invited member's Quicken ID"
            label='Email'
            rules={{
              required: true,
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: 'invalid email address',
              },
            }}
            sx={{ mt: 2 }}
            variant='filled'
          />
          <Box
            display='flex'
            alignItems='center'
            sx={{ backgroundColor: 'info.light', borderRadius: 1.5, color: 'text.secondary', mt: 2, p: 1 }}
          >
            <LabelImportantIcon />
            <Typography variant='captionRegular' sx={{ pl: 1 }}>
              By sharing your space, the invited member will have access to view and modify all of your accounts,
              transactions, and anything else within Simplifi.
            </Typography>
          </Box>
        </Box>
      </StdDialogContent>
      <StdDialogActions
        primary={{
          disabled: isSubmitting,
          children: 'Invite',
        }}
        secondary={{
          children: 'Cancel',
          onClick: onClose,
          variant: 'outlined',
        }}
      />
    </StdDialogForm>
  );

  const renderAddShareDone = () => (
    <StdDialogForm onSubmit={handleSubmit(onSubmit)}>
      <StdDialogContent
        id='add_share_done_dialog_content'
        sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}
      >
        <img alt='' src={addShareImage} width='168px' height='auto' />
        <Box display='flex' flex='1' flexDirection='column' alignItems='center' mx={16}>
          <Typography variant='headlineRegular' align='center'>
            {share.granteeFirstName}
          </Typography>
          <Typography variant='headlineRegular' align='center'>
            has been invited to {dataset.name} space.
          </Typography>
          <Typography variant='bodyRegular' align='center' sx={{ mt: 2 }}>
            You will be notified when {share.granteeFirstName} accepts your invite. The invite link expires in 7 days.
          </Typography>
        </Box>
      </StdDialogContent>
      <StdDialogActions
        primary={{
          children: 'Done',
          onClick: onClose,
        }}
      />
    </StdDialogForm>
  );

  return (
    <StdDialog disableBackdropClick onClose={onClose} open showCloseButton size='md' title='Invite member'>
      {!share ? renderAddShareForm() : renderAddShareDone()}
    </StdDialog>
  );
};

export default AddUserDialog;
